import React, { useState, useEffect, useCallback } from "react";
import styles from "./CompanyList.module.sass";
import { useNavigate } from "react-router-dom"; 
import cn from "classnames";
import Card from "../../components/Card";
import Form from "../../components/Form";
import Filters from "../../components/Filters";
import Icon from "../../components/Icon";

import Table from "./Table";
import Panel from "./Panel";
import Settings from "./Settings";

import { getProspectCompany } from "../../services/prospectCompany.service";

const ProspectCompanyList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [prospectCompanies, setProspectCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    sorting: "Plus récents",
    newStatus: false
  });

  const handleStatusChange = (id, newStatus) => {
    const updatedProspectCompanies = prospectCompanies.map(prospectCompany => 
      prospectCompany.id === id ? { ...prospectCompany, status: newStatus } : prospectCompany
    );
    setProspectCompanies(updatedProspectCompanies);
  };

  const fetchProspectCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoResults(false);

    try {
      // Construire les filtres
      let filterParts = [];
      
      // Filtre de recherche
      if (search.trim()) {
        filterParts.push(`(name~'${search.toLowerCase().trim()}' || sector~'${search.toLowerCase().trim()}')`);
      }
      
      // Filtre pour le statut "Nouveau"
      if (filters.newStatus) {
        filterParts.push(`status="Nouveau"`);
      }
      
      const filterString = filterParts.length > 0 ? filterParts.join(' && ') : null;
      
      // Déterminer le tri
      let sortString = "-created"; // Par défaut : plus récents
      if (filters.sorting === "Plus anciens") {
        sortString = "+created";
      }

      const result = await getProspectCompany({
        page,
        perPage: 10,
        filter: filterString,
        sort: sortString
      });
      
      setProspectCompanies(result.items || []); 
      setTotalPages(result.totalPages || 1); 
      setNoResults(result.items.length === 0 && !!search);

    } catch (err) {
      if (err.isAbort || (err.response && err.response.code === 0)) {
        console.warn('Request was autocancelled:', err.message);
      } else {
        console.error("Erreur lors du chargement des entreprises prospects:", err);
        setError("Impossible de charger les entreprises prospects");
        setProspectCompanies([]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, filters]); 
  
  useEffect(() => {
    fetchProspectCompanies();
  }, [fetchProspectCompanies]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setPage(1); 
    fetchProspectCompanies();
  };

  // Gestionnaire pour les changements de filtres
  const handleFiltersChange = (newFilters) => {
    console.log('Nouveaux filtres appliqués:', newFilters);
    setFilters(newFilters);
    setPage(1); // Remettre à la première page lors du changement de filtres
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <>
      <Card 
        className={styles.card} 
        title="Entreprises" 
        classTitle={cn("title-blue", styles.title)} 
        classCardHead={styles.head} 
        head={
          <>
            <Form
              className={styles.form}
              value={search}
              setValue={setSearch}
              onSubmit={handleSearch}
              placeholder="Rechercher par nom ou SIRET"
              type="text"
              name="search"
              icon="search"
            />
            <button 
              className={cn("button-stroke button-small", styles.button)} 
              onClick={() => navigate("/admin/prospects_company/create")}
            >
              Créer
            </button>
            <Filters
              className={styles.filters}
              title="Filtrer"
            >
              <Settings onFiltersChange={handleFiltersChange} />
            </Filters>
          </>
        }
      >
        {error && <div className={styles.error}>{error}</div>}
        
        {loading ? (
          <div className={styles.loading}>Chargement...</div>
        ) : (
          <div className={styles.row}>
            {noResults && prospectCompanies.length === 0 && (
              <div className={styles.empty}>
                Aucune entreprise prospect trouvée pour votre recherche "{search}".
              </div>
            )}
            {!noResults && prospectCompanies.length === 0 && !loading && !error && (
              <div className={styles.empty}>
                Aucune entreprise prospect disponible.
              </div>
            )}
            {prospectCompanies.length > 0 && (
              <>
                <Table 
                  className={styles.table} 
                  items={prospectCompanies} 
                  onStatusChange={handleStatusChange}
                  onSelectionChange={setSelectedIds}
                />

                <div className={styles.foot}>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.max(1, page - 1))} 
                    disabled={page === 1}
                  >
                    <Icon name="arrow-left" size="20" />
                  </button>
                  <span className={styles.pagination}>
                    Page {page} sur {totalPages}
                  </span>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.min(totalPages, page + 1))} 
                    disabled={page === totalPages}
                  >
                    <Icon name="arrow-right" size="20" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Card>
      <Panel 
        selectedIds={selectedIds} 
        onDeleteSuccess={() => {
          setSelectedIds([]);
          fetchProspectCompanies();
        }}
      />
    </>
  );
};

export default ProspectCompanyList;